import api from './api';

export interface WebAuthnCredential {
  id: string;
  name: string;
  created_at: string;
}

export interface RegistrationOptions {
  challenge: string;
  rp: {
    name: string;
    id: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: string;
    alg: number;
  }>;
  authenticatorSelection: {
    authenticatorAttachment: string;
    userVerification: string;
  };
  timeout: number;
  attestation: string;
}

export interface AuthenticationOptions {
  challenge: string;
  timeout: number;
  rpId: string;
  allowCredentials: Array<{
    type: string;
    id: string;
  }>;
  userVerification: string;
}

class WebAuthnService {
  /**
   * Check if WebAuthn is supported in the browser
   */
  isSupported(): boolean {
    return !!(navigator.credentials && window.PublicKeyCredential);
  }

  /**
   * Check if the device supports biometric authentication
   */
  async isBiometricAvailable(): Promise<boolean> {
    if (!this.isSupported()) return false;
    
    try {
      // Check if authenticatorGetInfo is available
      const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      return available;
    } catch (error) {
      console.warn('Error checking biometric availability:', error);
      return false;
    }
  }

  /**
   * Get registration options from the server
   */
  async getRegistrationOptions(): Promise<RegistrationOptions> {
    const response = await api.post('/webauthn/register/begin');
    console.log('Registration options response:', response.data);
    
    // Backend returns { success: true, options: {...} }
    if (response.data && response.data.success && response.data.options) {
      return response.data.options;
    }
    
    // Fallback for direct options
    return response.data;
  }

  /**
   * Register a new WebAuthn credential
   */
  async registerCredential(name?: string): Promise<{ success: boolean; message: string }> {
    if (!this.isSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    try {
      // Get registration options from server
      const options = await this.getRegistrationOptions();
      console.log('Registration options received:', options);
      console.log('User object:', options.user);
      console.log('User name:', options.user?.name, 'User displayName:', options.user?.displayName);
      
      // Validate the options structure
      if (!options.challenge) {
        throw new Error('Missing challenge in registration options');
      }
      if (!options.user || !options.user.id) {
        throw new Error('Missing user.id in registration options');
      }
      if (!options.user.name) {
        throw new Error('Missing user.name in registration options');
      }
      
      console.log('Challenge:', options.challenge, 'User ID:', options.user.id);
      
      // Convert challenge and user ID from base64url
      const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
        challenge: this.base64UrlToArrayBuffer(options.challenge),
        rp: options.rp,
        user: {
          ...options.user,
          id: this.base64UrlToArrayBuffer(options.user.id),
        },
        pubKeyCredParams: options.pubKeyCredParams.map(param => ({
          type: param.type as PublicKeyCredentialType,
          alg: param.alg,
        })),
        authenticatorSelection: {
          authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment,
          userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement,
        },
        timeout: options.timeout,
        attestation: options.attestation as AttestationConveyancePreference,
      };

      // Create credential
      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      const response = credential.response as AuthenticatorAttestationResponse;
      
      const registrationData = {
        response: {
          id: credential.id,
          rawId: this.arrayBufferToBase64Url(credential.rawId),
          response: {
            attestationObject: this.arrayBufferToBase64Url(response.attestationObject),
            clientDataJSON: this.arrayBufferToBase64Url(response.clientDataJSON),
          },
          type: credential.type,
        },
        deviceName: name || 'Biometric Device',
      };
      
      console.log('🔐 Sending registration data to backend:', registrationData);
      
      // Send credential to server
      const registrationResponse = await api.post('/webauthn/register/complete', registrationData);

      // Backend returns { success: true, message: "...", credential: {...} }
      if (registrationResponse.data && registrationResponse.data.success) {
        return {
          success: true,
          message: registrationResponse.data.message || 'Device registered successfully'
        };
      } else {
        return {
          success: false,
          message: registrationResponse.data.error || 'Registration failed'
        };
      }
    } catch (error: any) {
      console.error('WebAuthn registration error:', error);
      
      if (error.name === 'NotAllowedError') {
        throw new Error('Biometric registration was cancelled or not allowed');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('This device is already registered');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('This device does not support biometric authentication');
      }
      
      throw new Error(error.response?.data?.error || error.message || 'Registration failed');
    }
  }

  /**
   * Get authentication options from the server
   */
  async getAuthenticationOptions(): Promise<AuthenticationOptions> {
    const response = await api.post('/webauthn/authenticate/begin');
    
    // Backend returns { success: true, options: {...} }
    if (response.data && response.data.success && response.data.options) {
      return response.data.options;
    }
    
    // Fallback for direct options
    return response.data;
  }

  /**
   * Authenticate using WebAuthn
   */
  async authenticate(): Promise<{ token: string; user: any }> {
    if (!this.isSupported()) {
      throw new Error('WebAuthn is not supported in this browser');
    }

    try {
      // Get authentication options from server
      const options = await this.getAuthenticationOptions();
      
      // Convert challenge and credential IDs from base64url
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions = {
        challenge: this.base64UrlToArrayBuffer(options.challenge),
        timeout: options.timeout,
        rpId: options.rpId,
        allowCredentials: options.allowCredentials.map(cred => ({
          type: cred.type as PublicKeyCredentialType,
          id: this.base64UrlToArrayBuffer(cred.id),
        })),
        userVerification: options.userVerification as UserVerificationRequirement,
      };

      // Get assertion
      const assertion = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      }) as PublicKeyCredential;

      if (!assertion) {
        throw new Error('Authentication failed');
      }

      const response = assertion.response as AuthenticatorAssertionResponse;
      
      // Send assertion to server
      const authResponse = await api.post('/webauthn/authenticate/complete', {
        id: assertion.id,
        rawId: this.arrayBufferToBase64Url(assertion.rawId),
        response: {
          authenticatorData: this.arrayBufferToBase64Url(response.authenticatorData),
          clientDataJSON: this.arrayBufferToBase64Url(response.clientDataJSON),
          signature: this.arrayBufferToBase64Url(response.signature),
          userHandle: response.userHandle ? this.arrayBufferToBase64Url(response.userHandle) : null,
        },
        type: assertion.type,
      });

      return authResponse.data;
    } catch (error: any) {
      console.error('WebAuthn authentication error:', error);
      
      if (error.name === 'NotAllowedError') {
        throw new Error('Biometric authentication was cancelled');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('No registered devices found');
      }
      
      throw new Error(error.response?.data?.error || error.message || 'Authentication failed');
    }
  }

  /**
   * Get list of registered WebAuthn credentials
   */
  async getCredentials(): Promise<WebAuthnCredential[]> {
    try {
      const response = await api.get('/webauthn/credentials');
      console.log('WebAuthn API Response:', response);
      
      // Backend returns { success: true, credentials: [...] }
      if (response.data && response.data.success && Array.isArray(response.data.credentials)) {
        return response.data.credentials;
      }
      
      // Fallback in case of different response format
      if (Array.isArray(response.data)) {
        return response.data;
      }
      
      console.warn('Unexpected credentials response format:', response.data);
      return [];
    } catch (error) {
      console.error('WebAuthn credentials API error:', error);
      throw error;
    }
  }

  /**
   * Delete a WebAuthn credential
   */
  async deleteCredential(credentialId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/webauthn/credentials/${credentialId}`);
    
    // Backend returns { success: true/false, message: "..." }
    if (response.data && typeof response.data.success === 'boolean') {
      return {
        success: response.data.success,
        message: response.data.message || (response.data.success ? 'Credential deleted successfully' : 'Failed to delete credential')
      };
    }
    
    // Fallback
    return response.data;
  }

  /**
   * Utility: Convert base64url string to ArrayBuffer
   */
  private base64UrlToArrayBuffer(base64url: string): ArrayBuffer {
    if (!base64url || typeof base64url !== 'string') {
      throw new Error(`Invalid base64url input: ${base64url}`);
    }
    
    // Add padding if needed
    const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
    
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes.buffer;
  }

  /**
   * Utility: Convert ArrayBuffer to base64url string
   */
  private arrayBufferToBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binaryString = '';
    
    for (let i = 0; i < bytes.length; i++) {
      binaryString += String.fromCharCode(bytes[i]);
    }
    
    const base64 = btoa(binaryString);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }
}

export const webauthnService = new WebAuthnService();